import type { ReactNode } from 'react';

import { Spinner } from '@/components/atoms/Spinner';
import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';
import { useFeatureFlagGate } from '@/hooks/useFeatureFlagGate';

import { FeatureUnavailable } from './FeatureUnavailable';

interface FeatureFlagRouteProps {
    children?: ReactNode;
    flag: PostHogFeatureFlag;
    /** Where the unavailable state's link leads, normally the location dashboard. */
    unavailableLinkTo: string;
}

/**
 * Guards a route behind a PostHog feature flag.
 *
 * Nothing behind the flag renders until the flag is true. While the answer can still change
 * (flags loading, or a first false that may be the pre-identify answer) the loader shows; after
 * that an inline message shows instead of a redirect, so a late true can still open the page in
 * place. The message says whether the account was refused or PostHog never answered.
 */
export function FeatureFlagRoute({ children, flag, unavailableLinkTo }: FeatureFlagRouteProps) {
    // Admin bypass is handled via PostHog release conditions (isOptoScaleAdmin property),
    // not in code, which keeps flag logic in one place and changeable without deploys.
    const gate = useFeatureFlagGate(flag);

    if (gate === 'pending') {
        return <Spinner size="lg" />;
    }

    if (gate === 'enabled') {
        return <>{children}</>;
    }

    return <FeatureUnavailable linkTo={unavailableLinkTo} reason={gate} />;
}
